/**
 * SUNTRONIX ONE — One-click re-acceptance page.
 *
 * Landing page for the amendment email's "Review & re-accept" button.
 * The URL carries an opaque token that identifies both the affected
 * user and the agreement version they must re-accept.
 *
 * Flow:
 *   1. GET /api/legal/nudges/:token  →  { nudge, agreement }
 *   2. Show agreement body + signer name field + accept button
 *   3. POST /api/legal/nudges/:token/accept  →  records the fresh
 *      acceptance, marks the nudge ACCEPTED, and increments the
 *      campaign's completed count.
 *
 * The user must be logged in — if not, they are bounced to /login
 * with a `next=` param so they return here after auth.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ChevronDown, ShieldCheck, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ReacceptancePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState("loading"); // loading | ready | done | error | mismatch
  const [nudge, setNudge] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [error, setError] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await api.get(`/legal/nudges/${token}`);
      setNudge(data.nudge);
      setAgreement(data.agreement);
      setSignerName((prev) => prev || data.nudge.user_name || (data.nudge.user_email?.split("@")[0] ?? ""));
      setState(data.nudge.status === "ACCEPTED" ? "done" : "ready");
    } catch (err) {
      const code = err.response?.status;
      if (code === 401) {
        navigate(`/login?next=${encodeURIComponent(`/legal/reaccept/${token}`)}`, { replace: true });
        return;
      }
      if (code === 403) setState("mismatch");
      else setState("error");
      setError(err.response?.data?.detail || "Unable to load amendment link");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!authLoading) load();
  }, [authLoading, load]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 24 && !scrolledToBottom) setScrolledToBottom(true);
  };

  // If the amendment body fits the viewport, auto-satisfy the scroll gate.
  useEffect(() => {
    if (state !== "ready" || !agreement || scrolledToBottom) return;
    const check = () => {
      const el = scrollRef.current;
      if (!el) return;
      if (el.scrollHeight - el.clientHeight < 24) setScrolledToBottom(true);
    };
    const t1 = requestAnimationFrame(() => requestAnimationFrame(check));
    window.addEventListener("resize", check);
    return () => { cancelAnimationFrame(t1); window.removeEventListener("resize", check); };
  }, [state, agreement, scrolledToBottom]);

  const canSubmit = accepted && scrolledToBottom && signerName.trim().length >= 2 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post(`/legal/nudges/${token}/accept`, {
        signer_name: signerName.trim(),
        signer_title: signerTitle.trim() || undefined,
      });
      toast.success("Thank you — your acceptance has been recorded.");
      setState("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || state === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading amendment…
        </div>
      </div>
    );
  }

  if (state === "mismatch") {
    return (
      <ReacceptShell title="This link belongs to a different user">
        <p className="text-[13px] text-slate-600 leading-relaxed">
          You are signed in as <strong>{user?.email}</strong>. The amendment link was sent to a
          different user in your organisation. Please sign in with that email, or ask them to
          click the link from their inbox.
        </p>
        <ActionBtn onClick={() => navigate("/")} label="Return to SUNTRONIX ONE" />
      </ReacceptShell>
    );
  }

  if (state === "error") {
    return (
      <ReacceptShell title="Amendment link not found">
        <p className="text-[13px] text-slate-600 leading-relaxed">{error}</p>
        <ActionBtn onClick={() => navigate("/")} label="Return to SUNTRONIX ONE" />
      </ReacceptShell>
    );
  }

  if (state === "done") {
    return (
      <ReacceptShell title="You're all set" tone="success">
        <p className="text-[13px] text-slate-600 leading-relaxed">
          Your acceptance of <strong>{agreement?.title} ({agreement?.version})</strong> has been
          recorded. A signed PDF copy has been emailed to you for your records.
        </p>
        <ActionBtn onClick={() => navigate("/")} label="Continue to SUNTRONIX ONE" icon />
      </ReacceptShell>
    );
  }

  // ready state
  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-8 grid place-items-center" data-testid="reaccept-page">
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[92vh]">
        <header className="px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Amendment · Please re-accept</div>
            <h1 className="text-[15px] font-semibold text-slate-900 truncate" data-testid="reaccept-title">
              {agreement.title} <span className="text-slate-400 font-normal">· {agreement.version}</span>
            </h1>
            {nudge?.previous_version && (
              <div className="text-[11px] text-slate-500 mt-0.5">
                You previously accepted version {nudge.previous_version}.
              </div>
            )}
          </div>
        </header>

        <div ref={scrollRef} onScroll={handleScroll}
             className="flex-1 min-h-0 overflow-y-auto px-6 py-4 legal-body"
             data-testid="reaccept-body">
          {agreement.summary && (
            <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700">
              <span className="font-medium text-slate-900">Summary: </span>{agreement.summary}
            </div>
          )}
          <div className="prose prose-slate prose-sm max-w-none prose-headings:font-semibold prose-h2:text-[16px] prose-h3:text-[13px] prose-p:text-[13px] prose-p:leading-relaxed"
               dangerouslySetInnerHTML={{ __html: agreement.body_html || "" }} />
          {!scrolledToBottom && (
            <div className="sticky bottom-0 left-0 right-0 mt-4 flex justify-center pointer-events-none">
              <div className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-slate-900 text-white text-[11px] shadow-lg">
                <ChevronDown className="h-3.5 w-3.5 animate-bounce" />
                Scroll to the end to enable acceptance
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 shrink-0 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Your full name</label>
              <input type="text" value={signerName} onChange={(e) => setSignerName(e.target.value)}
                data-testid="reaccept-signer-name"
                className="w-full h-9 px-3 mt-1 rounded-md border border-slate-300 bg-white text-[13px] focus:outline-none focus:border-slate-900" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Title (optional)</label>
              <input type="text" value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)}
                data-testid="reaccept-signer-title"
                className="w-full h-9 px-3 mt-1 rounded-md border border-slate-300 bg-white text-[13px] focus:outline-none focus:border-slate-900" />
            </div>
          </div>
          <label className={"flex items-start gap-2 cursor-pointer " + (scrolledToBottom ? "" : "opacity-60 cursor-not-allowed")}>
            <input type="checkbox" disabled={!scrolledToBottom} checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="reaccept-checkbox"
              className="mt-0.5 h-4 w-4 accent-slate-900" />
            <span className="text-[12px] text-slate-700 leading-relaxed">
              I have read and agree to the <strong>{agreement.title} ({agreement.version})</strong>.
              {agreement.scope === "CORPORATE"
                ? " I confirm I am authorised to accept on behalf of my organisation."
                : " My electronic acceptance is binding."}
            </span>
          </label>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-slate-500">
              Evidence recorded: IP, browser, timestamp and cryptographic hash. PDF emailed on acceptance.
            </div>
            <button type="button" onClick={submit} disabled={!canSubmit}
              data-testid="reaccept-submit"
              className="h-9 px-4 rounded-md bg-slate-900 text-white text-[13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-800 flex items-center gap-1.5">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Accept and continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReacceptShell({ title, tone, children }) {
  const bg = tone === "success" ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200";
  const iconBg = tone === "success" ? "bg-emerald-500" : "bg-slate-900";
  return (
    <div className="min-h-screen grid place-items-center bg-slate-100 p-4" data-testid="reaccept-shell">
      <div className={"w-full max-w-md rounded-lg border shadow-sm p-6 " + bg}>
        <div className={"h-10 w-10 rounded-md grid place-items-center text-white mb-3 " + iconBg}>
          {tone === "success" ? <CheckCircle2 className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
        </div>
        <h1 className="text-[16px] font-semibold text-slate-900 mb-2">{title}</h1>
        {children}
      </div>
    </div>
  );
}

function ActionBtn({ onClick, label, icon }) {
  return (
    <button onClick={onClick} data-testid="reaccept-return"
      className="mt-4 h-9 px-4 rounded-md bg-slate-900 text-white text-[13px] font-medium hover:bg-slate-800 flex items-center gap-1.5">
      {label}
      {icon && <ArrowRight className="h-4 w-4" />}
    </button>
  );
}
